/**
 * Кассын сагс — баркод уншуулах ба уншуулсан гутлууд.
 *
 * ⚠️ Сагс нь ЗӨВХӨН клиент дээр (схемд сагсны хүснэгт байхгүй) — мөрүүд
 *    нь эцэг хуудсанд, энэ компонент зөвхөн нэмж хасна.
 *
 * ⚠️ Гутал зарагдах эсэхийг `sellableReason` (@boots/domain) шалгана —
 *    гар утасны POS ЯГ ТЭР функцийг дууддаг тул хоёр дэлгэц ижил
 *    шалтгаан хэлнэ. Эцсийн шийдвэр сервер дээр.
 *
 * ⚠️ СКАН = ГАР (keyboard-wedge). Сканнер нь цифрүүдийг хурдан «шивээд»
 *    Enter илгээдэг тул товчлуурын даралтыг ХУУДАС дээр барина — кассчин эхлээд
 *    талбар руу дарах шаардлагагүй. Өөр талбарт (хөнгөлөлт, утас) шивж
 *    байвал хөндөхгүй.
 *
 * ⚠️ `inputMode="none"` — Android-ын зөөлөн гар ГАРАХГҮЙ (D3 Pro,
 *    docs/ANDROID.md §10.3-ийн вэб дэх хувилбар). Биет сканнер нь HID
 *    төхөөрөмж тул энэ нь уншилтад нөлөөлөхгүй. Шошго урагдсан үед
 *    «Гараар шивэх» дарж гар гаргана.
 *
 * DARK GLASS (2026-10-05): мөр бүр зурагтай карт, шинэ мөр дээрээс
 * унаж орно. ⚠️ Дизайны «− 1 +» тоо ширхгийн товч ЭНД БАЙХГҮЙ —
 * гутал бүр давтагдашгүй биет нэгж (CLAUDE.md дүрэм 1), нэг мөр = нэг
 * гутал. Оронд нь баталгаа, хөнгөлөлт, хасах.
 */

import {
  useCallback, useEffect, useRef, useState,
  type Dispatch, type FormEvent, type ReactNode, type SetStateAction,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  WARRANTY_PRESETS, effectiveWarrantyDays, formatMnt, formatSerialDisplay, formatSize, isForeignScan,
  isValidSerial, nearestWarrantyPreset, normalizeSerialInput, sellableReason,
} from '@boots/domain';
import { ApiError, api, type Envelope } from '../lib/api';
import type { ItemDetail, ItemPhotoView } from '../lib/types';
import { Button, Card, CardTitle } from './ui';

/** Сканнер товчийг хэдхэн ms-ээр илгээнэ — үүнээс урт завсар = шинэ уншилт. */
const SCAN_BURST_GAP_MS = 300;

export interface CartLine {
  item: ItemDetail;
  discountMnt: string;
  /** Хаалттай жагсаалтаас (`WARRANTY_PRESETS`) — мөр хөлддөг тул чөлөөт шивэлт байхгүй. */
  warrantyDays: number;
}

export function SaleCart({
  lines,
  setLines,
  store,
  success,
  onScan,
}: {
  lines: CartLine[];
  setLines: Dispatch<SetStateAction<CartLine[]>>;
  /** Аль салбарын нэрээр зарах вэ — гутал тэнд байгаа эсэхийг шалгана. */
  store: { id: string; name: string } | null;
  /** Борлуулалт амжилттай болсны дараах харагдац — сагс хоосон үед сагсны оронд. */
  success?: ReactNode;
  /** Сагсанд гутал орох бүрд — эцэг хуудас амжилтын харагдацыг хаана. */
  onScan?: () => void;
}) {
  const scanRef = useRef<HTMLInputElement>(null);
  /** Уншуулж буй код. Хуудсан дээрх товчлуурын даралт энд хуримтлагдана. */
  const rawRef = useRef('');
  /**
   * Энэ уншилтад цифрээс өөр тэмдэгт орсон — шошгоны QR (Facebook-ийн
   * холбоос). Эхний цифрээс ӨМНӨХ үсэг талбарт хүрдэггүй тул энд тэмдэглэнэ.
   */
  const foreignRef = useRef(false);
  const lastKeyRef = useRef(0);
  const [raw, setRaw] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** Шошго урагдсан, баркод уншигдахгүй болсон үеийн ганц гарц. */
  const [manual, setManual] = useState(false);
  /** Амжилттай нэмэгдэх бүрд нэмэгдэнэ — талбар оюугаар анивчина. */
  const [flash, setFlash] = useState(0);

  const setValue = useCallback((v: string) => {
    rawRef.current = v;
    setRaw(v);
  }, []);

  const add = useCallback(
    async (value: string) => {
      const serial = normalizeSerialInput(value);
      const foreign = foreignRef.current || isForeignScan(value);
      foreignRef.current = false;
      setError(null);
      setValue('');

      // QR — гутлын код биш, ЧИМЭЭГҮЙ алгасна («код буруу» гэж хэлэхгүй)
      if (foreign) return;

      // Хэлбэр эхлээд — сүлжээгүй ч шивэлтийн алдаа энд баригдана
      if (!isValidSerial(serial)) {
        setError(serial ? `${serial} — гутлын код буруу байна` : 'Гутлын код буруу байна');
        return;
      }
      if (lines.some((l) => l.item.serial === serial)) {
        setError('Энэ гутал сагсанд аль хэдийн байна');
        return;
      }

      try {
        const res = await api<Envelope<ItemDetail>>(`/items/by-serial/${serial}`);
        const it = res.data;
        const why = sellableReason(it, store);
        if (why) {
          setError(`${formatSerialDisplay(serial)} — ${why}`);
          return;
        }
        setLines((prev) => [
          ...prev,
          {
            item: it,
            discountMnt: '0',
            warrantyDays: nearestWarrantyPreset(
              effectiveWarrantyDays(it.warrantyDaysOverride, it.model.warrantyDays),
            ),
          },
        ]);
        setFlash((n) => n + 1);
        onScan?.();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Хайлт амжилтгүй');
      }
    },
    [lines, setLines, setValue, store, onScan],
  );

  // Хуудсан дээр хаана ч уншуулахад ажиллана — талбар руу дарах хэрэггүй
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing =
        t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement ||
        t instanceof HTMLSelectElement || t?.isContentEditable === true;
      if (e.timeStamp - lastKeyRef.current > SCAN_BURST_GAP_MS) foreignRef.current = false;
      lastKeyRef.current = e.timeStamp;

      // Скан талбар өөрөө формоороо илгээнэ; бусад талбарт шивж байгааг хөндөхгүй
      if (typing) return;

      // QR-ийн үсэг, `/`, `:` — тэмдэглээд хөтөчид хүргэхгүй (`/` нь
      // Firefox-ийн хайлтыг нээж, дараагийн цифрүүдийг булаана)
      if (e.key.length === 1 && isForeignScan(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        foreignRef.current = true;
        return;
      }

      if (e.key === 'Enter') {
        // Сканнерын Enter нь фокустай товчийг (ж: «Зарах») дарахаас
        // сэргийлж — уншуулж эхэлсэн үед л барина
        if (!rawRef.current) return;
        e.preventDefault();
        void add(rawRef.current);
        return;
      }
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        setValue(rawRef.current + e.key);
        // Эхний цифрээр фокус талбарт шилжинэ — үлдсэн нь шууд тэнд бууна
        scanRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [add, setValue]);

  function submit(e: FormEvent) {
    e.preventDefault();
    void add(raw);
    scanRef.current?.focus();
  }

  const scanning = raw.length > 0;
  const patch = (id: string, v: Partial<CartLine>) =>
    setLines((prev) => prev.map((x) => (x.item.id === id ? { ...x, ...v } : x)));

  return (
    <div className="flex h-full flex-col gap-3.5">
      <Card className="sm:px-6 sm:py-5.5">
        <CardTitle
          right={
            <button
              type="button"
              onClick={() => {
                setManual((v) => !v);
                scanRef.current?.focus();
              }}
              className="inline-flex items-center gap-2 text-meta font-semibold text-accent-700 hover:underline"
            >
              <span aria-hidden className="h-2.5 w-3.5 rounded-xs border-[1.5px] border-current" />
              {manual ? 'Гар хаах' : 'Гараар шивэх'}
            </button>
          }
        >
          Баркод уншуулах
        </CardTitle>
        <form onSubmit={submit} className="flex gap-3">
          <div className="relative min-w-0 flex-1 overflow-hidden rounded-btn">
            <BarcodeGlyph active={scanning} />
            {/*
              Unbounded — зөвхөн цифр (баркод) ба Ө/Ү-гүй placeholder тул аюулгүй.
            */}
            <input
              ref={scanRef}
              value={raw}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Баркод уншуулна уу…"
              // «none» — Android-ын зөөлөн гар гарахгүй. Сканнер HID тул уншина
              inputMode={manual ? 'numeric' : 'none'}
              autoComplete="off"
              autoFocus
              aria-label="Гутлын баркод"
              className={`h-13 w-full rounded-btn border bg-input pr-4 pl-14.5 font-display text-lg font-medium
                tracking-[0.11em] text-ink transition placeholder:text-ink-3 focus:outline-none ${
                scanning
                  ? 'border-accent ring-4 ring-accent-100'
                  : 'border-hair focus:border-accent focus:ring-4 focus:ring-accent-100'
              }`}
            />
            {scanning ? <span aria-hidden className="scan-beam" /> : null}
            {flash ? (
              <span key={flash} aria-hidden className="scan-flash pointer-events-none absolute inset-0 bg-accent" />
            ) : null}
          </div>
          <Button type="submit" className="h-13 shrink-0 px-7 text-base font-bold">Сагсанд</Button>
        </form>
        <p className="mt-3 text-meta text-ink-3">
          Сканнераар уншуулна — дэлгэцийн аль ч хэсэгт, эхлээд энэ нүд рүү дарах шаардлагагүй.
        </p>
        {error ? <p role="alert" className="mt-2 text-sm text-alert-ink">{error}</p> : null}
      </Card>

      <Card className="flex min-h-105 flex-1 flex-col sm:px-6 sm:py-6">
        <CardTitle
          right={
            <span className="num text-meta font-semibold tracking-normal text-ink-3 normal-case">
              {lines.length} ширхэг
            </span>
          }
        >
          Сагс
        </CardTitle>

        {lines.length === 0 ? (
          (success ?? <EmptyCart />)
        ) : (
          <ul className="mt-2 flex flex-col gap-3.5">
            {lines.map((l) => (
              <CartRow
                key={l.item.id}
                line={l}
                onPatch={(v) => patch(l.item.id, v)}
                onRemove={() => setLines((prev) => prev.filter((x) => x.item.id !== l.item.id))}
              />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/** Баркодын тэмдэг — скан хийж байхад оюу болно. */
function BarcodeGlyph({ active }: { active: boolean }) {
  return (
    <span aria-hidden className="pointer-events-none absolute top-1/2 left-4.5 flex h-5 -translate-y-1/2 gap-0.5">
      {[2, 1, 3, 1, 2, 1, 1, 3].map((w, k) => (
        <span
          key={k}
          className={`h-full transition-colors ${active ? 'bg-accent' : 'bg-ink-3'} ${
            w === 1 ? 'w-px' : w === 2 ? 'w-0.5' : 'w-0.75'
          }`}
        />
      ))}
    </span>
  );
}

function EmptyCart() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center py-16 text-center">
      <div className="relative grid size-24 place-items-center">
        <span aria-hidden className="ping ping-slow absolute inset-0 rounded-full text-accent" />
        <span className="grid size-24 place-items-center rounded-full border border-accent-300 bg-accent-100">
          <span aria-hidden className="size-7.5 rotate-45 rounded-md border-[2.5px] border-accent" />
        </span>
      </div>
      {/* Unbounded — «Сагс хоосон»-д Ө/Ү байхгүй */}
      <p className="mt-7 font-display text-num-sm font-semibold">Сагс хоосон</p>
      <p className="mt-2.5 text-base text-ink-3">Гутлын баркодыг уншуулж борлуулалт эхлүүлнэ</p>
    </div>
  );
}

/**
 * Сагсны мөрийн зураг — `GET /items/:id/photos` (presigned, 10 мин).
 * Зураггүй, эсвэл эрхгүй бол судалтай орлуулагч.
 */
function useItemThumb(id: string): string | null {
  const q = useQuery({
    queryKey: ['item-thumb', id],
    queryFn: () => api<Envelope<ItemPhotoView[]>>(`/items/${id}/photos`),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const rows = q.data?.data ?? [];
  const p = rows.find((r) => r.role === 'PRIMARY') ?? rows[0];
  return p ? (p.thumbUrl ?? p.mediumUrl ?? p.originalUrl) : null;
}

function CartRow({ line, onPatch, onRemove }: {
  line: CartLine;
  onPatch: (v: Partial<CartLine>) => void;
  onRemove: () => void;
}) {
  const it = line.item;
  const thumb = useItemThumb(it.id);
  const discount = Number(line.discountMnt) || 0;
  const field =
    'h-10.5 rounded-field border border-hair bg-input px-3 text-base text-ink transition ' +
    'focus:border-accent focus:ring-4 focus:ring-accent-100 focus:outline-none sm:text-sm';

  return (
    <li className="cart-drop flex flex-wrap items-center gap-4 rounded-[18px] border border-hair bg-well p-4 lg:flex-nowrap lg:gap-5.5">
      <span className="grid size-20 shrink-0 place-items-center overflow-hidden rounded-frame border border-hair bg-[repeating-linear-gradient(135deg,var(--color-mute-bg)_0_6px,transparent_6px_12px)] sm:size-26">
        {thumb ? (
          <img src={thumb} alt="" className="size-full object-cover" loading="lazy" />
        ) : (
          <span className="font-mono text-micro text-ink-3">гутлын зураг</span>
        )}
      </span>

      <div className="min-w-0 flex-1 basis-48">
        <p className="text-title text-pretty">{it.model.brand} · {it.model.name}</p>
        <div className="mt-2.5 flex flex-wrap gap-2">
          <span className="rounded-[10px] bg-neutral-bg px-2.5 py-1 text-meta text-ink-2">
            Размер {formatSize(it.size)}
          </span>
          <span className="num rounded-[10px] bg-neutral-bg px-2.5 py-1 font-mono text-meta text-ink-3">
            {formatSerialDisplay(it.serial ?? '')}
          </span>
        </div>
      </div>

      <div className="flex items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-label text-ink-3">Баталгаа</span>
          <select
            value={line.warrantyDays}
            onChange={(e) => onPatch({ warrantyDays: Number(e.target.value) })}
            aria-label="Баталгаат хугацаа"
            className={field}
          >
            {WARRANTY_PRESETS.map((p) => (
              <option key={p.days} value={p.days}>{p.years} жил</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-label text-ink-3">Хөнгөлөлт ₮</span>
          <input
            value={line.discountMnt}
            onChange={(e) => onPatch({ discountMnt: e.target.value })}
            inputMode="decimal"
            aria-label="Мөрийн хөнгөлөлт"
            className={`num w-28 text-right ${field}`}
          />
        </label>
      </div>

      <div className="ml-auto flex min-w-36 flex-col items-end">
        <span className="font-display text-num-sm font-semibold">
          {formatMnt(Number(it.priceMnt) - discount)}
        </span>
        {discount > 0 ? (
          <span className="num text-meta text-ink-3 line-through">{formatMnt(it.priceMnt)}</span>
        ) : null}
      </div>

      <button
        type="button"
        onClick={onRemove}
        className="grid size-11 shrink-0 place-items-center rounded-full text-ink-3 transition
          hover:bg-alert-bg hover:text-alert-ink active:scale-[0.94] sm:size-9"
        aria-label="Сагснаас хасах"
        title="Сагснаас хасах"
      >
        ✕
      </button>
    </li>
  );
}
